import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  FormControlLabel,
  Switch,
  Alert,
  CircularProgress,
  Paper,
  Stack,
  MenuItem
} from '@mui/material';
import { Save, ArrowBack } from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { apiService } from '../../services/apiService';
import { ProductFormData } from '../../types';

// Función para parsear números con diferentes formatos
const parseNumberFrontend = (value: any): number | null => {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  
  const valueStr = String(value).trim();
  if (!valueStr) return null;
  
  // Manejar diferentes formatos de decimales
  let cleaned = valueStr;
  
  // Si tiene tanto coma como punto, determinar cuál es el separador decimal
  if (cleaned.includes(',') && cleaned.includes('.')) {
    const lastComma = cleaned.lastIndexOf(',');
    const lastDot = cleaned.lastIndexOf('.');
    
    if (lastComma > lastDot) {
      // Formato europeo: 1.234,56
      cleaned = cleaned.replace(/\./g, '').replace(',', '.');
    } else {
      // Formato US: 1,234.56
      cleaned = cleaned.replace(/,/g, '');
    }
  } else if (cleaned.includes(',')) {
    // Solo coma - asumir que es separador decimal
    cleaned = cleaned.replace(',', '.');
  }
  
  const parsed = parseFloat(cleaned);
  return isNaN(parsed) ? null : parsed;
};

// Función de validación personalizada para dimensiones
const validateDimension = (value: any, fieldName: string) => {
  const parsed = parseNumberFrontend(value);
  
  if (parsed === null) return true; // Es opcional
  
  if (parsed < 0) {
    return `${fieldName} debe ser positivo`;
  }
  
  if (parsed > 9999999) {
    return `${fieldName} no puede exceder 9,999,999 metros`;
  }
  
  // Validar decimales (máximo 3)
  const str = String(parsed);
  const decimalPart = str.split('.')[1];
  if (decimalPart && decimalPart.length > 3) {
    return `${fieldName} puede tener máximo 3 decimales`;
  }
  
  return true;
};

// Esquema de validación actualizado para la nueva estructura
const productSchema = yup.object().shape({
  sku: yup.string(),
  codigo: yup.string().required('El código es requerido'),
  color: yup.string(),
  grupo: yup.string(),
  categoria: yup.string(),
  descripcion: yup.string(),
  stock: yup.number().min(0, 'El stock no puede ser negativo').integer('El stock debe ser un número entero'),
  largo: yup.mixed().test('valid-dimension', 'Dimensión inválida', function(value) {
    const result = validateDimension(value, 'El largo');
    return result === true ? true : this.createError({ message: result });
  }),
  ancho: yup.mixed().test('valid-dimension', 'Dimensión inválida', function(value) {
    const result = validateDimension(value, 'El ancho');
    return result === true ? true : this.createError({ message: result });
  }),
  espesor: yup.mixed().test('valid-dimension', 'Dimensión inválida', function(value) {
    const result = validateDimension(value, 'El espesor');
    return result === true ? true : this.createError({ message: result });
  }),
  precio_unitario: yup.number().required('El precio unitario es requerido').min(0, 'El precio debe ser mayor a 0'),
  precio_m2: yup.number().min(0, 'El precio por m2 debe ser positivo'),
  currency: yup.string().oneOf(['USD', 'EUR', 'ARS', 'BRL'], 'Moneda no válida'),
  descuento: yup.number().min(0, 'El descuento no puede ser negativo').max(100, 'El descuento no puede superar 100%'),
  estructura_m2: yup.string().test(
    'valid-structure',
    'El formato debe ser: codigo,cantidad (uno por línea)',
    function(value) {
      if (!value) return true; // Es opcional
      
      const lines = value.split('\n').filter(line => line.trim());
      return lines.every(line => {
        const parts = line.trim().split(',');
        return parts.length === 2 && 
               parts[0].trim() && 
               !isNaN(Number(parts[1].trim())) && 
               Number(parts[1].trim()) > 0;
      });
    }
  ),
  is_active: yup.boolean(),
});

// Inferir el tipo del esquema (no lo usamos pero lo dejamos para referencia futura)

export const ProductForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);
  
  const [loading, setLoading] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  const {
    control,
    handleSubmit,
    reset,
    setError,
    clearErrors,
    formState: { errors, isSubmitting }
  } = useForm<ProductFormData>({
    // Usar validación manual mejorada
    defaultValues: {
      sku: '',
      codigo: '',
      color: '',
      grupo: '',
      categoria: '',
      descripcion: '',
      stock: 0,
      largo: undefined,
      ancho: undefined,
      espesor: undefined,
      precio_unitario: 0,
      precio_m2: undefined,
      currency: 'USD',
      descuento: 0,
      estructura_m2: '',
      is_active: true
    }
  });

  useEffect(() => {
    if (isEdit && id) {
      loadProduct(parseInt(id));
    }
  }, [id, isEdit]);

  const loadProduct = async (productId: number) => {
    try {
      setLoading(true);
      setServerError(null);
      
      const productData = await apiService.products.getById(productId);
      
      reset({
        codigo: productData.codigo,
        sku: productData.sku || '',
        color: productData.color || '',
        grupo: productData.grupo || '',
        categoria: productData.categoria || '',
        descripcion: productData.descripcion || '',
        stock: productData.stock || 0,
        largo: productData.largo,
        ancho: productData.ancho,
        espesor: productData.espesor,
        precio_unitario: productData.precio_unitario,
        precio_m2: productData.precio_m2,
        descuento: productData.descuento || 0,
        estructura_m2: productData.estructura_m2 || '',
        is_active: productData.is_active
      } as any);
    } catch (err: any) {
      console.error('Error loading product:', err);
      setServerError('Error al cargar el producto');
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (data: any) => {
    try {
      setServerError(null);
      clearErrors();
      
      // Validar y parsear dimensiones
      const largoValidation = validateDimension(data.largo, 'El largo');
      if (largoValidation !== true) {
        setError('largo', { message: largoValidation });
        return;
      }
      
      const anchoValidation = validateDimension(data.ancho, 'El ancho');
      if (anchoValidation !== true) {
        setError('ancho', { message: anchoValidation });
        return;
      }
      
      const espesorValidation = validateDimension(data.espesor, 'El espesor');
      if (espesorValidation !== true) {
        setError('espesor', { message: espesorValidation });
        return;
      }
      
      const apiData: ProductFormData = {
        sku: data.sku,
        codigo: data.codigo,
        color: data.color,
        grupo: data.grupo,
        categoria: data.categoria,
        descripcion: data.descripcion,
        stock: data.stock,
        largo: parseNumberFrontend(data.largo) || undefined,
        ancho: parseNumberFrontend(data.ancho) || undefined,
        espesor: parseNumberFrontend(data.espesor) || undefined,
        precio_unitario: data.precio_unitario,
        precio_m2: data.precio_m2,
        descuento: data.descuento,
        estructura_m2: data.estructura_m2,
        is_active: data.is_active
      };
            <Controller
              name="descripcion"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label="Descripción"
                  multiline
                  minRows={2}
                  maxRows={4}
                />
              )}
            />
      
      if (isEdit && id) {
        await apiService.products.update(parseInt(id), apiData);
      } else {
        await apiService.products.create(apiData);
      }
      
      navigate('/products');
    } catch (err: any) {
      console.error('Error saving product:', err);
      setServerError(err.response?.data?.detail || 'Error al guardar el producto');
    }
  };

  const handleBack = () => {
    navigate('/products');
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 800, mx: 'auto' }}>
      <Box display="flex" alignItems="center" mb={3}>
        <Button
          startIcon={<ArrowBack />}
          onClick={handleBack}
          sx={{ mr: 2 }}
        >
          Volver
        </Button>
        <Typography variant="h4" component="h1">
          {isEdit ? 'Editar Producto' : 'Nuevo Producto'}
        </Typography>
      </Box>

      {serverError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {serverError}
        </Alert>
      )}

  <Paper sx={{ p: 3, borderRadius: 4 }}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Stack spacing={3}>
            <Controller
              name="codigo"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label="Código *"
                  error={!!errors.codigo}
                  helperText={errors.codigo?.message}
                />
              )}
            />

            <Controller
              name="sku"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label="SKU"
                />
              )}
            />

            <Box display="flex" gap={2}>
              <Controller
                name="color"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Color"
                  />
                )}
              />

              <Controller
                name="grupo"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Grupo"
                  />
                )}
              />

              <Controller
                name="categoria"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Categoría"
                  />
                )}
              />
            </Box>

            <Box display="flex" gap={2}>
              <Controller
                name="precio_unitario"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Precio Unitario *"
                    type="number"
                    inputProps={{ step: "0.01" }}
                    error={!!errors.precio_unitario}
                    helperText={errors.precio_unitario?.message}
                  />
                )}
              />

              <Controller
                name="stock"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Stock"
                    type="number"
                  />
                )}
              />
            </Box>

            {/* Sección de Dimensiones */}
            <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
              Dimensiones (en metros)
            </Typography>
            
            <Alert severity="info" sx={{ mb: 2 }}>
              <strong>Formato de números:</strong> Puedes usar punto (.) o coma (,) como separador decimal.
              Ejemplos válidos: 3.66, 3,66, 1.234,56, 1,234.56. Máximo 3 decimales y 9,999,999 metros.
            </Alert>
            
            <Box display="flex" gap={2}>
              <Controller
                name="largo"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Largo (m)"
                    type="text"
                    placeholder="3.66 o 3,66"
                    error={!!errors.largo}
                    helperText={errors.largo?.message || "Longitud en metros (ej: 3.66 o 3,66)"}
                  />
                )}
              />

              <Controller
                name="ancho"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Ancho (m)"
                    type="text"
                    placeholder="0.138 o 0,138"
                    error={!!errors.ancho}
                    helperText={errors.ancho?.message || "Anchura en metros (ej: 0.138 o 0,138)"}
                  />
                )}
              />

              <Controller
                name="espesor"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Espesor (m)"
                    type="text"
                    placeholder="0.02 o 0,02"
                    error={!!errors.espesor}
                    helperText={errors.espesor?.message || "Grosor en metros (ej: 0.02 o 0,02)"}
                  />
                )}
              />
            </Box>

            {/* Sección de Precios */}
            <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
              Precios y Descuentos
            </Typography>
            
            <Box display="flex" gap={2}>
              <Controller
                name="precio_m2"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Precio por M²"
                    type="number"
                    inputProps={{ step: "0.01", min: "0" }}
                    helperText="Precio por metro cuadrado"
                  />
                )}
              />

              <Controller
                name="currency"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    select
                    label="Moneda"
                    fullWidth
                    helperText="Moneda de los precios"
                    sx={{ minWidth: 120 }}
                  >
                    <MenuItem value="USD">USD ($)</MenuItem>
                    <MenuItem value="EUR">EUR (€)</MenuItem>
                    <MenuItem value="ARS">ARS ($)</MenuItem>
                    <MenuItem value="BRL">BRL (R$)</MenuItem>
                  </TextField>
                )}
              />

              <Controller
                name="descuento"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Descuento (%)"
                    type="number"
                    inputProps={{ step: "0.01", min: "0", max: "100" }}
                    error={!!errors.descuento}
                    helperText={errors.descuento?.message || "Porcentaje de descuento (0-100)"}
                  />
                )}
              />
            </Box>

            {/* Sección de Estructura */}
            <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
              Estructura por M²
            </Typography>
            
            <Controller
              name="estructura_m2"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label="Lista de productos para estructura"
                  multiline
                  rows={4}
                  placeholder="Formato: codigo,cantidad (uno por línea)
Ejemplo:
PGC80X0.90,1
COBRA21,21"
                  error={!!errors.estructura_m2}
                  helperText={errors.estructura_m2?.message || "Un producto por línea con formato: codigo,cantidad"}
                />
              )}
            />

            <Controller
              name="is_active"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Switch
                      checked={field.value}
                      onChange={field.onChange}
                    />
                  }
                  label="Producto activo"
                />
              )}
            />

            <Box display="flex" gap={2} justifyContent="flex-end" sx={{ mt: 3 }}>
              <Button
                variant="outlined"
                onClick={handleBack}
                disabled={isSubmitting}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                variant="contained"
                startIcon={isSubmitting ? <CircularProgress size={20} /> : <Save />}
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Guardando...' : (isEdit ? 'Actualizar' : 'Crear')}
              </Button>
            </Box>
          </Stack>
        </form>
      </Paper>
    </Box>
  );
};
